import type { AxiosInstance, AxiosRequestConfig, AxiosRequestTransformer } from 'axios';
import type {
  AxiosInterceptorOptions,
  AxiosInterceptorType,
  Constructor,
} from './api.types';
import axios from 'axios';
import { autoBindDecorator } from './api.utils';

function useSetHeader(instance: AxiosInstance) {
  return (key: string, value: string) => {
    delete instance.defaults.headers[key];
    delete instance.defaults.headers.common[key];
    instance.defaults.headers.common[key] = value;
  };
}

function useSetInterceptors(instance: AxiosInstance) {
  return (interceptors: AxiosInterceptorOptions) => {
    for (const [type, interceptorsTuples] of Object.entries(interceptors)) {
      if (interceptorsTuples?.length === 0) continue;
      for (const tuple of interceptorsTuples) {
        instance.interceptors[type as AxiosInterceptorType].use(...tuple);
      }
    }
  };
}

function mergeSerializers(config: AxiosRequestConfig) {
  const { transformRequest, transformResponse } = config;
  if (Array.isArray(transformRequest)) {
    config.transformRequest = [
      ...transformRequest,
      ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
    ];
  }
  if (Array.isArray(transformResponse)) {
    config.transformResponse = [
      ...(axios.defaults.transformResponse as AxiosRequestTransformer[]),
      ...transformResponse,
    ];
  }
}

export function useHttp(config: AxiosRequestConfig, interceptors?: AxiosInterceptorOptions) {
  mergeSerializers(config);
  const httpService = axios.create(config);

  const setHeader = useSetHeader(httpService);
  const setInterceptors = useSetInterceptors(httpService);

  if (interceptors) {
    setInterceptors(interceptors);
  }

  const defineService = (constructor: Constructor, args: unknown[] = []) => {
    const constructorArgs = [httpService, ...args];
    const instance = Reflect.construct(constructor, constructorArgs);
    console.log(`⚡ ${constructor.name} defined`);
    return autoBindDecorator(instance);
  };

  return {
    httpService,
    defineService,
    setHeader,
    setInterceptors,
  };
}
